<template>
  <div class="annotation-content">
    <div class="header-wrapper">
      <div class="name" :class="{ 'learning-fa': lfaAppName }">
        {{ ownerName }}
      </div>
      <div class="actions">
        <cv-button
          v-if="props.canEdit"
          class="icon-button"
          size="small"
          kind="ghost"
          @click="handleEdit"
        >
          <svg-icon name="Pencil24" kind="action" />
        </cv-button>
      </div>
    </div>

    <div class="timestamp">
      <div class="timestamp-wrapper">
        <div v-if="isTeamAnnotation" class="team-tag">
          <keywords :tags="teamTag" />
        </div>
      </div>
    </div>
    <div class="note">
      <rich-text :content="note" :expandable="false" :config="richTextConfig" />
    </div>
    <div v-if="!isEditorContext" class="keywords">
      <label>{{ t("annotation_keywords") }}</label>
      <div v-if="hasKeywords" class="output-text">
        <keywords :tags="keywordsTags" />
      </div>
      <div v-else>
        {{ emptyKeywordLabel }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Keywords from "@plugins/keywords/components/Keywords.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { AnchoringEventBus } from "@/modules/annotation/service/anchoring.event.bus";
import { isEmpty } from "@/utils/var.check";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import {
  ANCHORING_CONTEXT_TYPE,
  ANNOTATION_ANCHOR_ACTION,
  ANNOTATION_MODE,
  ANNOTATION_TYPE,
  OWNER_TYPE,
} from "@modules/annotation/service/annotation.constant";
import { isAnchorInQuiz } from "@modules/annotation/service/annotation.anchor.utils.ts";
import RichText from "@plugins/richtext/components/RichText.vue";
import { useDirty } from "@/plugins/common/composables/page-dirty";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";

interface Props {
  annotation?: object;
  canEdit?: boolean;
  tooltipData?: object;
}

const props = withDefaults(defineProps<Props>(), {
  annotation: () => null,
  canEdit: false,
  tooltipData: () => null,
});

const { t } = useI18n();

const adapter = useAnnotationAttemptAdapter();
const annotationHandler = useAnnotation(undefined, adapter);
const { isEditorContext } = useAnnotationPermissions();
const { promptPageDirty } = useDirty(ref(""));
const store = useStore();
const contentSubpage = useContentSubpageInstance();

const emptyKeywordLabel = ref("-");

const getIntegratorAppAccessByAppFeatureId = computed(
  () => store.getters["app/getIntegratorAppAccessByAppFeatureId"],
);

const richTextConfig = computed(() => ({
  plugins: {},
}));

const lfaAppName = computed(
  () =>
    getIntegratorAppAccessByAppFeatureId.value(props.annotation?.appFeatureId)?.appFeatureName ??
    "",
);
const ownerName = computed(() =>
  isEmpty(props.annotation?.appFeatureId) ? props.annotation?.creatorName : lfaAppName.value,
);

const hasKeywords = computed(() => props.annotation?.keywords?.length);
const keywordsTags = computed(() => props.annotation?.keywords);
const note = computed(() => props.annotation?.note ?? "");

const isTeamAnnotation = computed(() => props.annotation?.ownerType === OWNER_TYPE.TEAM);
const teamTag = computed(() => t("annotation_team_tag_label"));

function handleEdit() {
  promptPageDirty().then((response) => {
    if (!response) {
      return;
    }
    contentSubpage.removeAll();
    annotationHandler.setWithNote(true);
    annotationHandler.setSelectedAnnotation({
      annotation: props.annotation,
      mode: ANNOTATION_MODE.EDIT,
    });

    if (
      [
        ANNOTATION_TYPE.TEXT_ANNOTATION,
        ANNOTATION_TYPE.IMAGE_ANNOTATION,
        ANNOTATION_TYPE.TOOLTIP_ANNOTATION,
      ].includes(props.annotation?.type)
    ) {
      if (isAnchorInQuiz(props.annotation?.anchor ?? "")) {
        AnchoringEventBus.$emit("anchorToQuiz", {
          contextType: ANCHORING_CONTEXT_TYPE.ANNOTATION,
          data: props.annotation?.anchor,
        });
      } else {
        AnchoringEventBus.$emit("anchorToPaginatedActivity", {
          contextType: ANCHORING_CONTEXT_TYPE.ANNOTATION,
          data: {
            annotation: props.annotation,
            action: ANNOTATION_ANCHOR_ACTION.EDIT,
          },
        });
      }
    }
  });
}
</script>
